import { Component, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { PageEvent } from '@angular/material/paginator';
import { FinaliseImportedProductsRequest, ImportedProduct, ListPagedImportedProductsRequest, PurgeImportedProductsRequest, SituViewWebApiClient } from 'situview-webapi-client';
import { AuthService, BreadcrumbService, NotificationService } from 'src/app/services';
import { Role } from 'src/app/shared/models/role.model';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'product-import-preview',
  templateUrl: './product-import-preview.component.html',
  styleUrls: ['./product-import-preview.component.scss']
})

export class ProductImportPreviewComponent implements OnInit {

  private _loading = false;
  public get loading(): boolean {
    return this._loading;
  }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  viewControls = {
    pageIndex: 0,
    pageSize: 25,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100],
    displayedColumns: ['description', 'sku', 'displaySku', 'type', 'brand', 'disabled', 'import']
  } as ProductsListViewControls;

  previousSearchTerm = "";
  searchTerm = "";

  // We're using excluded products here so that we don't need to enumerate all included
  // products since we're using paged data.
  excludedProducts: (string | undefined)[] = [];

  admin = false;
  productsDataSource = new MatTableDataSource<ImportedProductWithImportToggle>();

  constructor(
    private situViewWebApiClient: SituViewWebApiClient,
    private authService: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private breadcrumbService: BreadcrumbService,
    private snackBar: MatSnackBar,
    private notificationService: NotificationService) {
    this.admin = (this.authService.hasRole(Role.SituSystemsDeveloper) || this.authService.hasRole(Role.SituSystemsAdmin));
    this.loadProducts();
  }

  ngOnInit(): void {
  }

  loadProducts() {
    this.loading = true;
    let request = { searchTerm: this.searchTerm } as ListPagedImportedProductsRequest;
    if (this.searchTermChanged()) this.resetSearch();
    request.pageNumber = this.viewControls.pageIndex + 1;
    request.pageSize = this.viewControls.pageSize;
    this.previousSearchTerm = this.searchTerm;

    return this.situViewWebApiClient.listPagedImportedProducts(request).then(response => {
      this.productsDataSource.data = (response.items as ImportedProductWithImportToggle[] | undefined) ?? [];
      this.viewControls.totalItems = response.totalItems ?? 0;
      this.loading = false;
      this.productsDataSource.data.forEach(p => {
        p.import = !p.errors?.length && this.excludedProducts.indexOf(p.id) === -1;
      });
    }, error => {
      this.loading = false;
    });
  }

  onImportClick() {
    this.updateExcludedProducts();
    let req = { excludedProductsIds: this.excludedProducts } as FinaliseImportedProductsRequest;
    this.situViewWebApiClient.finaliseImportedProducts(req).then(response => {
      if (response.success) {
        this.notificationService.updateImportedProductsNotification()
        this.showMessage(`${response.productCount} products imported`)
        this.router.navigate([`/products/dashboard`]);
      }
      else {
        this.showMessage('There was an issue importing your products')
      }
    })
  }

  onPurgeImportClick() {
    let req = {} as PurgeImportedProductsRequest;
    this.situViewWebApiClient.purgeImportedProducts(req).then(response => {
      if (response.success) {
        this.notificationService.updateImportedProductsNotification();
        this.showMessage(`All imported products purged`)
        this.router.navigate([`/products/dashboard`]);
      }
      else {
        this.showMessage('There was an issue purging imported products')
      }
    })
  }

  onCancelClick() {
    this.router.navigate([`/products/dashboard`]);
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;
    this.updateExcludedProducts();
    this.loadProducts();
  }

  updateExcludedProducts() {
    this.productsDataSource.data.forEach(p => {
      const isExcluded = !p.import;
      const hasNoError = !p.errors?.length;
      const isUnique = this.excludedProducts.indexOf(p.id) === -1;

      if (isExcluded && hasNoError && isUnique) {
        this.excludedProducts.push(p.id);
      }
    });
  }

  resetSearch() {
    this.viewControls.pageIndex = 0;
    this.viewControls.pageSize = 25;
    this.viewControls.totalItems = 0;
  }
  private searchTermChanged(): boolean {
    return this.searchTerm.toLowerCase() !== this.previousSearchTerm.toLowerCase();
  }

  showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }

}

interface ProductsListViewControls {
  pageSize: number;
  pageIndex: number;
  pageNumber: number;
  totalItems: number;
  pageSizeOptions: number[];
  displayedColumns: string[];
}

interface ImportedProductWithImportToggle extends ImportedProduct {
  import: boolean;
}
